/**
 * useFieldRegistration Hook
 *
 * Main state management hook for the Field Registration module.
 * Manages location loading, selection, editing, and progress tracking.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fieldRegistrationService } from '../services/fieldRegistrationService';
import { stcBinService } from '../services/stcBinService';
import { StcBinDto } from '../types/stcBin';
import { StcBinTypeDto } from '../services/stcBinTypeService';
import { StcBinSizeDto } from '../types/stcBinSize';
import { StcBinGrpDto } from '../services/stcBinGrpService';
import { StcMovementTypeDto } from '../services/stcMovementTypeService';
import { RackSummary } from '../components/warehouse/modules/rack-builder/types';
import {
  FieldRegistrationLocation,
  FieldRegistrationView,
  FieldRegistrationSummary,
  LocationFilter,
  EditableBinProperties,
  EditableItemCapacity,
  FieldRegistrationItem,
} from '../types/fieldRegistration';

interface UseFieldRegistrationReturn {
  // State
  view: FieldRegistrationView;
  setView: (view: FieldRegistrationView) => void;
  loading: boolean;
  error: string | null;
  allBins: StcBinDto[];
  binTypes: StcBinTypeDto[];
  binSizes: StcBinSizeDto[];
  binGroups: StcBinGrpDto[];
  movementTypes: StcMovementTypeDto[];
  filteredBins: StcBinDto[];
  selectedLocation: FieldRegistrationLocation | null;
  summary: FieldRegistrationSummary | null;
  filter: LocationFilter;
  saving: boolean;
  successMessage: string | null;

  // Actions
  loadWarehouseData: (warehouseID: string) => Promise<void>;
  selectLocation: (bin: StcBinDto) => Promise<void>;
  selectLocationByBinCode: (binCode: string) => Promise<void>;
  clearSelection: () => void;
  updateFilter: (updates: Partial<LocationFilter>) => void;
  updateBinProperties: (properties: EditableBinProperties) => Promise<boolean>;
  updateItemCapacity: (
    itemNo: string,
    binSizeNo: number,
    existingId: number | null,
    capacity: EditableItemCapacity
  ) => Promise<boolean>;
  updateItemStdBinCode: (itemNo: string, binCode: string) => Promise<boolean>;
  bulkApplyCapacity: (
    items: FieldRegistrationItem[],
    binSizeNo: number,
    capacity: EditableItemCapacity,
    applyToMissingOnly: boolean
  ) => Promise<{ success: number; failed: number }>;
  refreshLocation: () => Promise<void>;
  clearError: () => void;
  clearSuccess: () => void;
  rackSummaries: RackSummary[];
}

const DEFAULT_FILTER: LocationFilter = {
  searchTerm: '',
  completionFilter: 'all',
  sortBy: 'binCode',
};

export function useFieldRegistration(): UseFieldRegistrationReturn {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core state
  const [view, setView] = useState<FieldRegistrationView>('overview');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Data state
  const [allBins, setAllBins] = useState<StcBinDto[]>([]);
  const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
  const [binSizes, setBinSizes] = useState<StcBinSizeDto[]>([]);
  const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
  const [movementTypes, setMovementTypes] = useState<StcMovementTypeDto[]>([]);
  const [rackSummaries, setRackSummaries] = useState<RackSummary[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<FieldRegistrationLocation | null>(null);
  const [filter, setFilter] = useState<LocationFilter>(DEFAULT_FILTER);

  // Completion cache: binCode → completionPercent (avoids re-loading every location)
  const completionCacheRef = useRef<Map<string, number>>(new Map());

  // Summary (computed from cache when available)
  const [summary, setSummary] = useState<FieldRegistrationSummary | null>(null);

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // ── Load Warehouse Data ────────────────────────────────────────

  const loadWarehouseData = useCallback(async (warehouseID: string) => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);

    try {
      // Load bins, bin types, bin sizes, groups, movement types, rack summaries, and field registration summary in parallel
      const [bins, types, sizes, groups, mvTypes, racks, summaryData] = await Promise.all([
        fieldRegistrationService.loadLocations(companyNo, warehouseID),
        fieldRegistrationService.getBinTypes(companyNo),
        fieldRegistrationService.getBinSizes(companyNo),
        fieldRegistrationService.getBinGroups(companyNo),
        fieldRegistrationService.getMovementTypes(companyNo),
        stcBinService.getRackSummary(companyNo, warehouseID),
        stcBinService.getFieldRegistrationSummary(companyNo, warehouseID),
      ]);

      setAllBins(bins);
      setBinTypes(types);
      setBinSizes(sizes);
      setBinGroups(groups);
      setMovementTypes(mvTypes);
      setRackSummaries(racks);

      // Use real summary data from backend
      setSummary({
        totalLocations: summaryData.totalLocations || bins.length,
        completedLocations: 0,
        totalItems: summaryData.totalItems,
        itemsWithCapacity: summaryData.itemsWithCapacity,
        itemsMissingCapacity: summaryData.itemsMissingCapacity,
        locationsWithOverrides: 0,
        locationsUsingDefaults: bins.length,
      });

      console.log(`[useFieldRegistration] Loaded ${bins.length} bins, ${types.length} types, ${sizes.length} sizes, ${groups.length} groups, ${mvTypes.length} movement types, ${racks.length} racks, summary: ${summaryData.totalItems} items (${summaryData.itemsWithCapacity} with capacity)`);
    } catch (err: any) {
      console.error('[useFieldRegistration] Failed to load warehouse data:', err);
      setError(err.message || 'Kunne ikke laste lagerdata');
    } finally {
      setLoading(false);
    }
  }, [companyNo]);

  // ── Select Location ────────────────────────────────────────────

  const selectLocation = useCallback(async (bin: StcBinDto) => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);

    try {
      const detail = await fieldRegistrationService.buildLocationDetail(companyNo, bin);
      setSelectedLocation(detail);

      // Update completion cache
      completionCacheRef.current.set(bin.binCode, detail.completionStatus.completionPercent);

      setView('location-detail');
    } catch (err: any) {
      console.error(`[useFieldRegistration] Failed to load location ${bin.binCode}:`, err);
      setError(err.message || `Kunne ikke laste lokasjon ${bin.binCode}`);
    } finally {
      setLoading(false);
    }
  }, [companyNo]);

  const selectLocationByBinCode = useCallback(async (binCode: string) => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);

    try {
      const bin = await fieldRegistrationService.loadLocationByBinCode(companyNo, binCode);
      if (!bin) {
        setError(`Lokasjon "${binCode}" ble ikke funnet`);
        setLoading(false);
        return;
      }

      const detail = await fieldRegistrationService.buildLocationDetail(companyNo, bin);
      setSelectedLocation(detail);
      completionCacheRef.current.set(bin.binCode, detail.completionStatus.completionPercent);
      setView('location-detail');
    } catch (err: any) {
      console.error(`[useFieldRegistration] Failed to load location ${binCode}:`, err);
      setError(err.message || `Kunne ikke laste lokasjon ${binCode}`);
    } finally {
      setLoading(false);
    }
  }, [companyNo]);

  const clearSelection = useCallback(() => {
    setSelectedLocation(null);
    setView('location-list');
  }, []);

  // ── Refresh Current Location ───────────────────────────────────

  const refreshLocation = useCallback(async () => {
    if (!selectedLocation || !companyNo) return;

    try {
      const detail = await fieldRegistrationService.buildLocationDetail(
        companyNo,
        selectedLocation.bin
      );
      setSelectedLocation(detail);
      completionCacheRef.current.set(
        selectedLocation.bin.binCode,
        detail.completionStatus.completionPercent
      );
    } catch (err: any) {
      console.error('[useFieldRegistration] Failed to refresh location:', err);
    }
  }, [selectedLocation, companyNo]);

  // ── Update Operations ──────────────────────────────────────────

  const updateBinProperties = useCallback(async (properties: EditableBinProperties): Promise<boolean> => {
    if (!selectedLocation || !companyNo) return false;

    setSaving(true);
    setError(null);

    try {
      const updatedBin = await fieldRegistrationService.updateBinProperties(
        companyNo,
        selectedLocation.bin.warehouseID,
        selectedLocation.bin.binCode,
        selectedLocation.bin,
        properties
      );

      // Refresh the full location detail with the updated bin
      const detail = await fieldRegistrationService.buildLocationDetail(companyNo, updatedBin);
      setSelectedLocation(detail);
      completionCacheRef.current.set(updatedBin.binCode, detail.completionStatus.completionPercent);

      // Also update the bin in allBins
      setAllBins(prev => prev.map(b => b.binCode === updatedBin.binCode ? updatedBin : b));

      setSuccessMessage('Lokasjonsegenskaper oppdatert');
      return true;
    } catch (err: any) {
      console.error('[useFieldRegistration] Failed to update bin properties:', err);
      setError(err.message || 'Kunne ikke oppdatere lokasjonsegenskaper');
      return false;
    } finally {
      setSaving(false);
    }
  }, [selectedLocation, companyNo]);

  const updateItemCapacity = useCallback(async (
    itemNo: string,
    binSizeNo: number,
    existingId: number | null,
    capacity: EditableItemCapacity
  ): Promise<boolean> => {
    if (!companyNo) return false;

    setSaving(true);
    setError(null);

    try {
      await fieldRegistrationService.updateItemCapacity(
        companyNo,
        itemNo,
        binSizeNo,
        existingId,
        capacity
      );

      // Refresh location to get updated data
      await refreshLocation();
      setSuccessMessage(`Kapasitet oppdatert for ${itemNo}`);
      return true;
    } catch (err: any) {
      console.error(`[useFieldRegistration] Failed to update capacity for ${itemNo}:`, err);
      setError(err.message || `Kunne ikke oppdatere kapasitet for ${itemNo}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [companyNo, refreshLocation]);

  const bulkApplyCapacity = useCallback(async (
    items: FieldRegistrationItem[],
    binSizeNo: number,
    capacity: EditableItemCapacity,
    applyToMissingOnly: boolean
  ): Promise<{ success: number; failed: number }> => {
    if (!companyNo) return { success: 0, failed: 0 };

    setSaving(true);
    setError(null);

    try {
      const result = await fieldRegistrationService.bulkApplyCapacity(
        companyNo,
        items,
        binSizeNo,
        capacity,
        applyToMissingOnly
      );

      // Refresh location
      await refreshLocation();

      if (result.failed > 0) {
        setSuccessMessage(`${result.success} oppdatert, ${result.failed} feilet`);
      } else {
        setSuccessMessage(`${result.success} varer oppdatert`);
      }

      return result;
    } catch (err: any) {
      console.error('[useFieldRegistration] Bulk apply failed:', err);
      setError(err.message || 'Masseregistrering feilet');
      return { success: 0, failed: 0 };
    } finally {
      setSaving(false);
    }
  }, [companyNo, refreshLocation]);

  const updateItemStdBinCode = useCallback(async (itemNo: string, binCode: string): Promise<boolean> => {
    if (!companyNo) return false;

    setSaving(true);
    setError(null);

    try {
      await fieldRegistrationService.updateItemStdBinCode(companyNo, itemNo, binCode);
      await refreshLocation();
      setSuccessMessage(`Fast plukklokasjon satt for ${itemNo}`);
      return true;
    } catch (err: any) {
      console.error(`[useFieldRegistration] Failed to update StdBinCode for ${itemNo}:`, err);
      setError(err.message || `Kunne ikke oppdatere fast plukklokasjon for ${itemNo}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [companyNo, refreshLocation]);

  // ── Filter & Sort ──────────────────────────────────────────────

  const updateFilter = useCallback((updates: Partial<LocationFilter>) => {
    setFilter(prev => ({ ...prev, ...updates }));
  }, []);

  const filteredBins = (() => {
    let result = [...allBins];

    // Text search
    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      result = result.filter(b =>
        b.binCode.toLowerCase().includes(term) ||
        (b.description1 && b.description1.toLowerCase().includes(term))
      );
    }

    // Zone filter
    if (filter.zoneId) {
      result = result.filter(b => b.stcZoneID === filter.zoneId);
    }

    // Rack prefix filter (rackPrefix is e.g. "01", BinCodes are "010101")
    if (filter.rackPrefix) {
      result = result.filter(b => b.binCode.startsWith(filter.rackPrefix!));
    }

    // Bin type filter
    if (filter.binTypeNo) {
      result = result.filter(b => b.binTypeNo === filter.binTypeNo);
    }

    // Bin size filter
    if (filter.binSizeNo) {
      result = result.filter(b => b.binSizeNo === filter.binSizeNo);
    }

    // Sort
    if (filter.sortBy === 'binCode') {
      result.sort((a, b) => a.binCode.localeCompare(b.binCode));
    }

    return result;
  })();

  // ── Error/Success Clearing ─────────────────────────────────────

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccessMessage(null), []);

  return {
    view,
    setView,
    loading,
    error,
    allBins,
    binTypes,
    binSizes,
    binGroups,
    movementTypes,
    filteredBins,
    selectedLocation,
    summary,
    filter,
    saving,
    successMessage,
    loadWarehouseData,
    selectLocation,
    selectLocationByBinCode,
    clearSelection,
    updateFilter,
    updateBinProperties,
    updateItemCapacity,
    updateItemStdBinCode,
    bulkApplyCapacity,
    refreshLocation,
    clearError,
    clearSuccess,
    rackSummaries,
  };
}
